/**
 * menuTheme — the customer menu's design engine.
 *
 * A restaurant chooses:
 *   • menuTheme  : 'classic' | 'modern' | 'elegant'   (three distinct designs)
 *   • menuLayout : 'horizontal' | 'sidebar'           (category bar orientation)
 *   • menuColors : { mode:'dark'|'light', bg, surface, text, accent }  (full palette)
 *
 * Everything is rendered from CSS custom properties so any colour combination
 * works. This module turns a restaurant document into:
 *   resolveMenuTheme(restaurant) → { theme, layout, colors }
 *   menuCssVars(colors)          → React style object of CSS variables
 *   buildMenuCss(theme, layout)  → the full themed stylesheet for the menu page
 */

export const MENU_THEMES = [
  { id: 'classic', name: 'Classic',      desc: 'Dark, image-led cards in a grid — the original look.' },
  { id: 'modern',  name: 'Modern',       desc: 'Bright and airy with large imagery and soft shadows.' },
  { id: 'elegant', name: 'Elegant List', desc: 'Fine-dining printed-menu rows with serif type.' },
];
export const MENU_LAYOUTS = [
  { id: 'horizontal', name: 'Horizontal bar', desc: 'Categories scroll across the top.' },
  { id: 'sidebar',    name: 'Side bar',       desc: 'Categories stacked in a left column.' },
];

// ── colour helpers ────────────────────────────────────────────────────────────
function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return null;
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}
function rgba(hex, a) {
  const c = hexToRgb(hex);
  return c ? `rgba(${c.r},${c.g},${c.b},${a})` : hex;
}
/** Returns black or white — whichever is readable on top of `hex`. */
function readableOn(hex) {
  const c = hexToRgb(hex);
  if (!c) return '#ffffff';
  const L = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
  return L > 0.6 ? '#1a1a1a' : '#ffffff';
}

// ── resolve palette (with sensible defaults derived from primaryColor) ─────────
function resolveMenuColors(restaurant) {
  const c = (restaurant && restaurant.menuColors) || {};
  const accent = c.accent || restaurant?.primaryColor || '#c8a96e';
  const mode = c.mode === 'light' ? 'light' : 'dark';
  const dark = mode === 'dark';

  const bg      = c.bg      || (dark ? '#0f0f0f' : '#f7f4ef');
  const surface = c.surface || (dark ? '#191a1d' : '#ffffff');
  const text    = c.text    || (dark ? '#f5f0e8' : '#1f1c19');

  return {
    mode, accent, bg, surface, text,
    accentText: readableOn(accent),
    muted:      rgba(text, 0.52),
    faint:      rgba(text, 0.32),
    border:     rgba(text, 0.12),
    surface2:   rgba(text, dark ? 0.06 : 0.035),
    navBg:      rgba(bg, 0.9),
    accentSoft: rgba(accent, 0.14),
    shadow:     dark ? 'rgba(0,0,0,0.4)' : 'rgba(60,50,40,0.14)',
  };
}

export function resolveMenuTheme(restaurant) {
  const theme  = ['classic', 'modern', 'elegant'].includes(restaurant?.menuTheme) ? restaurant.menuTheme : 'classic';
  const layout = restaurant?.menuLayout === 'sidebar' ? 'sidebar' : 'horizontal';
  return { theme, layout, colors: resolveMenuColors(restaurant) };
}

/** CSS custom properties to attach to the .menu-page root element. */
export function menuCssVars(colors) {
  return {
    '--bg': colors.bg,
    '--nav-bg': colors.navBg,
    '--surface': colors.surface,
    '--surface-2': colors.surface2,
    '--text': colors.text,
    '--muted': colors.muted,
    '--faint': colors.faint,
    '--border': colors.border,
    '--accent': colors.accent,
    '--accent-text': colors.accentText,
    '--accent-soft': colors.accentSoft,
    '--shadow': colors.shadow,
    '--primary': colors.accent, // backward-compat alias used by existing rules
  };
}

// ── stylesheet ─────────────────────────────────────────────────────────────────
export function buildMenuCss() {
  return `
    /* ══ BASE (shared structure, fully themed via CSS vars) ══════════════════ */
    .menu-page { min-height:100vh; max-width:100%; overflow-x:hidden; background:var(--bg); color:var(--text); font-family:'DM Sans',sans-serif; padding-bottom:110px; transition:background .3s,color .3s; }

    .menu-nav { display:flex; align-items:center; gap:1rem; padding:1rem 2rem; background:var(--nav-bg); backdrop-filter:blur(20px); border-bottom:1px solid var(--border); position:sticky; top:0; z-index:100; }
    .back-btn { background:none; border:none; color:var(--muted); cursor:pointer; font-family:'DM Sans',sans-serif; font-size:.9rem; white-space:nowrap; padding:.5rem; border-radius:8px; transition:color .2s,background .2s; max-width:45vw; overflow:hidden; text-overflow:ellipsis; flex-shrink:0; }
    .back-btn:hover { color:var(--text); background:var(--surface-2); }
    .nav-search { flex:1 1 180px; min-width:0; display:flex; align-items:center; background:var(--surface-2); border:1px solid var(--border); border-radius:10px; padding:0 1rem; gap:.5rem; }
    .search-icon { font-size:.9rem; opacity:.5; }
    .search-input { background:none; border:none; outline:none; color:var(--text); font-family:'DM Sans',sans-serif; font-size:.9rem; width:100%; padding:.6rem 0; }
    .search-input::placeholder { color:var(--faint); }
    .nav-actions { display:flex; gap:.6rem; align-items:center; flex-shrink:0; margin-left:auto; }
    .icon-btn, .cart-btn, .profile-btn { position:relative; width:42px; height:42px; border-radius:50%; border:1px solid var(--border); background:var(--surface-2); font-size:1.1rem; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .2s; color:var(--text); flex-shrink:0; }
    .icon-btn:hover, .cart-btn:hover, .profile-btn:hover { background:var(--accent-soft); }
    .nav-search-btn { display:none; }   /* desktop: inline search is shown instead */
    .cart-badge { position:absolute; top:-4px; right:-4px; background:var(--accent); color:var(--accent-text); border-radius:50%; width:18px; height:18px; font-size:.65rem; font-weight:700; display:flex; align-items:center; justify-content:center; }
    .avatar { width:28px; height:28px; background:var(--accent); border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:.8rem; font-weight:700; color:var(--accent-text); }

    /* body wrapper lets the category bar switch between top-bar and sidebar */
    .menu-body { display:block; }

    .category-bar { display:flex; gap:.5rem; overflow-x:auto; padding:1rem 2rem; scrollbar-width:none; border-bottom:1px solid var(--border); }
    .category-bar::-webkit-scrollbar { display:none; }
    .cat-pill { display:flex; align-items:center; gap:.5rem; padding:.6rem 1.2rem; border-radius:25px; border:1px solid var(--border); background:none; color:var(--muted); cursor:pointer; white-space:nowrap; font-family:'DM Sans',sans-serif; transition:all .2s; flex-shrink:0; }
    .cat-pill.active { background:var(--accent); color:var(--accent-text); border-color:var(--accent); font-weight:600; }
    .cat-pill:hover:not(.active) { background:var(--surface-2); color:var(--text); }
    .cat-icon { font-size:1.1rem; }
    .cat-name { font-size:.85rem; }

    .menu-content { min-width:0; }

    .subcategory-bar { display:flex; gap:.5rem; overflow-x:auto; padding:.75rem 2rem; background:var(--surface-2); scrollbar-width:none; }
    .subcategory-bar::-webkit-scrollbar { display:none; }
    .sub-pill { padding:.4rem 1rem; border-radius:20px; border:1px solid var(--border); background:none; color:var(--muted); cursor:pointer; font-size:.8rem; white-space:nowrap; font-family:'DM Sans',sans-serif; transition:all .2s; }
    .sub-pill.active { background:var(--accent-soft); color:var(--text); border-color:var(--accent); }
    .sub-pill:hover { color:var(--text); }

    .section-header { display:flex; align-items:baseline; gap:1rem; padding:1.5rem 2rem .75rem; flex-wrap:wrap; }
    .section-title { font-family:'Playfair Display',serif; font-size:1.6rem; font-weight:700; color:var(--text); }
    .section-sub { color:var(--faint); font-size:.85rem; }
    .item-count { margin-left:auto; background:var(--surface-2); padding:.25rem .75rem; border-radius:20px; font-size:.75rem; color:var(--muted); }

    .products-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1.25rem; padding:1rem 2rem 2rem; }
    .empty-state { grid-column:1/-1; text-align:center; padding:4rem 2rem; color:var(--faint); }

    /* ── Recommended-for-you strip ─────────────────────────────────────────── */
    .rec-strip { padding:1.25rem 2rem .25rem; }
    .rec-strip-head { display:flex; align-items:center; gap:.5rem; margin-bottom:.75rem; }
    .rec-strip-title { font-family:'Playfair Display',serif; font-size:1.15rem; font-weight:700; color:var(--text); }
    .rec-strip-badge { font-size:.62rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; color:var(--accent); background:var(--accent-soft); padding:.2rem .5rem; border-radius:999px; }
    .rec-row { display:flex; gap:.85rem; overflow-x:auto; scrollbar-width:none; padding-bottom:.5rem; }
    .rec-row::-webkit-scrollbar { display:none; }
    .rec-card { flex:0 0 auto; width:150px; background:var(--surface); border:1px solid var(--border); border-radius:14px; overflow:hidden; cursor:pointer; transition:transform .2s, box-shadow .2s; }
    .rec-card:hover { transform:translateY(-3px); box-shadow:0 8px 24px var(--shadow); }
    .rec-card-img { width:100%; height:96px; object-fit:cover; display:block; background:var(--surface-2); }
    .rec-card-fallback { width:100%; height:96px; display:flex; align-items:center; justify-content:center; font-size:2rem; background:var(--surface-2); }
    .rec-card-body { padding:.6rem .7rem .75rem; }
    .rec-card-name { font-size:.82rem; font-weight:600; color:var(--text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .rec-card-price { font-size:.82rem; font-weight:700; color:var(--accent); margin-top:.2rem; }
    @media (max-width:600px) { .rec-strip { padding-left:1rem; padding-right:1rem; } }

    /* ── Motion system ───────────────────────────────────────────────────────
       Cards float up with a stagger as a category renders; images shimmer while
       loading; buttons give tactile press feedback; the cart badge pops when it
       changes. All of it is disabled for prefers-reduced-motion users. */
    @keyframes cardIn { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
    @keyframes badgePop { 0% { transform:scale(.4); } 60% { transform:scale(1.25); } 100% { transform:scale(1); } }
    @keyframes shimmer { from { background-position:200% 0; } to { background-position:-200% 0; } }
    @keyframes contentIn { from { opacity:0; } to { opacity:1; } }
    .menu-content { animation:contentIn .35s ease both; }
    .products-grid .product-card { animation:cardIn .45s cubic-bezier(.2,.7,.3,1) both; }
    .products-grid .product-card:nth-child(1) { animation-delay:.03s; }
    .products-grid .product-card:nth-child(2) { animation-delay:.07s; }
    .products-grid .product-card:nth-child(3) { animation-delay:.11s; }
    .products-grid .product-card:nth-child(4) { animation-delay:.15s; }
    .products-grid .product-card:nth-child(5) { animation-delay:.19s; }
    .products-grid .product-card:nth-child(6) { animation-delay:.23s; }
    .products-grid .product-card:nth-child(n+7) { animation-delay:.27s; }
    .cart-badge { animation:badgePop .3s cubic-bezier(.2,.7,.3,1); }
    .qty-btn:active, .add-btn:active, .item-qty-btn:active, .cat-pill:active { transform:scale(.92); }
    .cat-pill { transition:all .2s cubic-bezier(.2,.7,.3,1); }
    @media (prefers-reduced-motion: reduce) {
      .menu-content, .products-grid .product-card, .cart-badge { animation:none !important; }
      .product-card, .card-img { transition:none !important; }
    }

    /* ── Product card (classic base) ─────────────────────────────────────── */
    .product-card { background:var(--surface); border:1px solid var(--border); border-radius:16px; overflow:hidden; cursor:pointer; transition:transform .25s cubic-bezier(.2,.7,.3,1),border-color .25s,box-shadow .25s; display:flex; flex-direction:column; }
    .product-card:hover { transform:translateY(-4px); border-color:var(--accent); box-shadow:0 12px 36px var(--shadow); }
    .product-card.unavailable { opacity:.5; }
    .card-img-wrap { position:relative; height:180px; overflow:hidden; flex-shrink:0; background:linear-gradient(110deg,var(--surface-2) 35%,var(--surface) 50%,var(--surface-2) 65%); background-size:200% 100%; animation:shimmer 1.6s linear infinite; }
    .card-img { width:100%; height:100%; object-fit:cover; transition:transform .45s cubic-bezier(.2,.7,.3,1); position:relative; }
    .product-card:hover .card-img { transform:scale(1.06); }
    .card-img-fallback { width:100%; height:100%; display:flex; align-items:center; justify-content:center; font-size:3rem; background:var(--surface-2); }
    .unavailable-overlay { position:absolute; inset:0; background:rgba(0,0,0,.6); display:flex; align-items:center; justify-content:center; color:#fff; font-size:.85rem; font-weight:600; letter-spacing:.1em; text-transform:uppercase; }
    .card-tags { position:absolute; top:.75rem; left:.75rem; display:flex; gap:.4rem; flex-wrap:wrap; }
    .tag { padding:.2rem .6rem; border-radius:12px; font-size:.65rem; font-weight:700; text-transform:uppercase; letter-spacing:.05em; background:var(--surface-2); color:var(--muted); }
    .tag-bestseller, .tag-popular { background:var(--accent); color:var(--accent-text); }
    .tag-vegetarian { background:#2d6a4f; color:#b7e4c7; }
    .tag-vegan { background:#1b4332; color:#95d5b2; }
    .tag-spicy { background:#7f1d1d; color:#fca5a5; }
    .tag-gluten-free { background:#3d2b1f; color:#d4a574; }
    .tag-chef-s-choice, .tag-premium { background:#1e1b4b; color:#a5b4fc; }
    .card-body { padding:1.25rem; flex:1; display:flex; flex-direction:column; }
    .card-name { font-family:'Playfair Display',serif; font-size:1.05rem; font-weight:700; color:var(--text); margin-bottom:.5rem; }
    .card-name-orig { font-size:.72rem; color:var(--muted); opacity:.75; margin:-.35rem 0 .5rem; font-weight:600; }
    .card-branch-tag { display:inline-block; font-size:.68rem; font-weight:700; color:var(--primary,#c8a96e); background:color-mix(in srgb, var(--primary,#c8a96e) 14%, transparent); border:1px solid color-mix(in srgb, var(--primary,#c8a96e) 30%, transparent); padding:.15rem .5rem; border-radius:6px; margin:0 0 .5rem; max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .orig-names-toggle { margin-left:.5rem; background:var(--surface-2); color:var(--muted); border:1px solid var(--border,rgba(0,0,0,.1)); padding:.25rem .7rem; border-radius:20px; font-size:.72rem; font-weight:700; cursor:pointer; transition:all .15s; }
    .orig-names-toggle:hover { color:var(--text); }
    .orig-names-toggle.on { background:var(--primary,#c8a96e); color:#fff; border-color:transparent; }
    html[dir="rtl"] .orig-names-toggle { margin-left:0; margin-right:.5rem; }
    .card-desc { font-size:.8rem; color:var(--muted); line-height:1.5; flex:1; margin-bottom:1rem; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
    .card-options { display:flex; flex-wrap:wrap; gap:.35rem; margin-bottom:.75rem; }
    .card-option { display:inline-flex; align-items:center; gap:.25rem; padding:.18rem .5rem; border-radius:999px; background:color-mix(in srgb, var(--accent) 12%, transparent); border:1px solid color-mix(in srgb, var(--accent) 28%, transparent); color:var(--accent); font-size:.7rem; font-weight:600; line-height:1.2; }
    .card-option-icon { font-size:.85rem; }
    .card-footer { display:flex; align-items:center; justify-content:space-between; gap:.5rem; flex-wrap:wrap; }
    .card-price { font-size:1.15rem; font-weight:700; color:var(--accent); min-width:0; overflow-wrap:anywhere; }
    .qty-control { display:flex; align-items:center; gap:.5rem; flex-shrink:0; }
    .qty-btn { width:30px; height:30px; border-radius:50%; border:1px solid var(--border); background:var(--surface-2); color:var(--text); font-size:1.1rem; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .2s; line-height:1; }
    .qty-btn:hover { background:var(--accent-soft); }
    .qty-btn-add { background:var(--accent); border-color:var(--accent); color:var(--accent-text); }
    .qty-btn-add:hover { opacity:.85; }
    .qty-num { font-weight:700; font-size:1rem; color:var(--text); min-width:20px; text-align:center; }
    .add-btn { padding:.45rem 1rem; border-radius:20px; border:1px solid var(--accent); background:transparent; color:var(--accent); font-family:'DM Sans',sans-serif; font-size:.85rem; font-weight:600; cursor:pointer; transition:all .2s; }
    .add-btn:hover { background:var(--accent); color:var(--accent-text); }

    .floating-cart { position:fixed; bottom:2rem; left:50%; transform:translateX(-50%); background:var(--accent); color:var(--accent-text); border-radius:30px; padding:1rem 2rem; display:flex; gap:2rem; align-items:center; font-weight:600; font-size:.95rem; cursor:pointer; box-shadow:0 8px 30px var(--shadow); transition:transform .2s,box-shadow .2s; z-index:50; animation:slideUp .3s ease; }
    @keyframes slideUp { from { transform:translateX(-50%) translateY(20px); opacity:0; } to { transform:translateX(-50%) translateY(0); opacity:1; } }
    .floating-cart:hover { transform:translateX(-50%) translateY(-2px); }

    /* ══ THEME: MODERN ═══════════════════════════════════════════════════════ */
    .theme-modern .cat-pill { border-radius:12px; text-transform:uppercase; letter-spacing:.04em; font-size:.78rem; padding:.65rem 1.1rem; }
    .theme-modern .section-title { font-family:'DM Sans',sans-serif; font-weight:800; letter-spacing:-.02em; }
    .theme-modern .products-grid { gap:1.75rem; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); }
    .theme-modern .product-card { border-radius:22px; border:none; background:var(--surface); box-shadow:0 10px 30px var(--shadow); }
    .theme-modern .product-card:hover { transform:translateY(-5px); box-shadow:0 18px 44px var(--shadow); }
    .theme-modern .card-img-wrap { height:210px; }
    .theme-modern .card-name { font-family:'DM Sans',sans-serif; font-weight:800; font-size:1.1rem; }
    .theme-modern .card-price { background:var(--accent-soft); color:var(--accent); padding:.25rem .7rem; border-radius:10px; font-size:1rem; }
    .theme-modern .add-btn { background:var(--accent); color:var(--accent-text); border-color:var(--accent); }

    /* ══ THEME: ELEGANT LIST ════════════════════════════════════════════════ */
    .theme-elegant .menu-page, .theme-elegant.menu-page { font-family:'Playfair Display',serif; }
    .theme-elegant .products-grid { display:block; max-width:820px; margin:0 auto; padding:1rem 2rem 2rem; }
    .theme-elegant .product-card { flex-direction:row; align-items:center; gap:1rem; background:none; border:none; border-bottom:1px solid var(--border); border-radius:0; padding:1.1rem .25rem; overflow:visible; }
    .theme-elegant .product-card:hover { transform:none; box-shadow:none; border-color:var(--border); }
    .theme-elegant .product-card:hover .card-img { transform:none; }
    .theme-elegant .card-img-wrap { width:64px; height:64px; border-radius:10px; flex-shrink:0; }
    .theme-elegant .card-img-fallback { font-size:1.5rem; border-radius:10px; }
    .theme-elegant .card-tags { position:static; margin-top:.35rem; }
    .theme-elegant .card-body { padding:0; flex-direction:column; }
    .theme-elegant .card-name { font-size:1.15rem; margin-bottom:.2rem; }
    .theme-elegant .card-desc { font-family:'DM Sans',sans-serif; -webkit-line-clamp:2; margin-bottom:0; }
    /* dotted leader between name block and price */
    .theme-elegant .card-footer { position:relative; align-items:flex-end; }
    .theme-elegant .card-body { position:relative; }
    .theme-elegant .card-price { font-family:'DM Sans',sans-serif; font-weight:700; white-space:nowrap; }
    .theme-elegant .qty-control { margin-left:.75rem; }
    .theme-elegant .section-title { font-size:1.9rem; }

    /* ══ LAYOUT: SIDEBAR ═════════════════════════════════════════════════════ */
    .layout-sidebar .menu-body { display:grid; grid-template-columns:240px 1fr; align-items:start; }
    .layout-sidebar .category-bar { flex-direction:column; overflow:visible; gap:.4rem; padding:1.25rem 1rem; border-bottom:none; border-right:1px solid var(--border); position:sticky; top:74px; align-self:start; max-height:calc(100vh - 74px); overflow-y:auto; }
    .layout-sidebar .cat-pill { width:100%; justify-content:flex-start; border-radius:10px; }
    .layout-sidebar .menu-content { border-left:none; }
    @media (max-width:768px) {
      .layout-sidebar .menu-body { display:block; }
      .layout-sidebar .category-bar { flex-direction:row; overflow-x:auto; position:static; max-height:none; border-right:none; border-bottom:1px solid var(--border); padding:1rem; }
      .layout-sidebar .cat-pill { width:auto; border-radius:25px; }
    }

    /* Phone numbers are always written left-to-right, even in RTL languages */
    input[type="tel"] { direction:ltr; text-align:left; }

    /* ── Favorites heart ─────────────────────────────────────────────────── */
    .fav-btn { position:absolute; top:.6rem; right:.6rem; width:34px; height:34px; border-radius:50%; border:none; background:rgba(0,0,0,.45); backdrop-filter:blur(6px); color:#fff; font-size:1.05rem; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:transform .15s, background .2s; z-index:2; }
    .fav-btn:hover { transform:scale(1.12); }
    .fav-btn.on { color:#f87171; background:rgba(0,0,0,.6); }
    html[dir="rtl"] .fav-btn { right:auto; left:.6rem; }

    /* ── Share popover ───────────────────────────────────────────────────── */
    .share-wrap { position:relative; }
    .share-pop { position:absolute; top:calc(100% + .5rem); right:0; background:var(--surface); border:1px solid var(--border); border-radius:14px; padding:.4rem; display:flex; flex-direction:column; gap:2px; min-width:170px; z-index:300; box-shadow:0 14px 44px var(--shadow); }
    html[dir="rtl"] .share-pop { right:auto; left:0; }
    .share-opt { display:flex; align-items:center; gap:.55rem; padding:.6rem .75rem; background:none; border:none; border-radius:10px; color:var(--text); font-family:'DM Sans',sans-serif; font-size:.85rem; cursor:pointer; text-decoration:none; text-align:start; }
    .share-opt:hover { background:var(--surface-2); }

    /* ── Powered-by badge ────────────────────────────────────────────────── */
    .powered-by { display:block; text-align:center; padding:2rem 1rem 2.5rem; font-size:.74rem; color:var(--faint); text-decoration:none; letter-spacing:.04em; transition:color .2s; }
    .powered-by strong { color:var(--accent); font-weight:700; }
    .powered-by:hover { color:var(--muted); }

    /* ══ RESPONSIVE ══════════════════════════════════════════════════════════ */
    @media (max-width:600px) {
      .products-grid, .theme-modern .products-grid { grid-template-columns:1fr; padding:1rem; }
      .category-bar, .subcategory-bar, .section-header, .menu-nav { padding-left:1rem; padding-right:1rem; }
      .theme-elegant .products-grid { padding:1rem; }

      /* Smart compact nav: the inline search collapses to a 🔍 icon so the bar
         stays one tidy row. Tapping it drops a full-width search bar underneath. */
      .menu-nav { gap:.5rem; padding-top:.7rem; padding-bottom:.7rem; }
      .back-btn { font-size:.85rem; max-width:38vw; }
      .nav-search-btn { display:flex; }
      /* The search drops to its OWN full-width row inside the nav (in flow, not
         absolute) so it never overlaps the category bar — both stay readable. */
      .menu-nav { flex-wrap:wrap; }
      .nav-search { order:10; flex-basis:100%; min-width:100%; margin-top:.55rem; padding:.1rem 1rem; display:none; }
      .menu-nav.search-open .nav-search { display:flex; }
      .menu-nav.search-open .nav-search .search-input { padding:.6rem 0; }
    }

    /* Extra-narrow phones: trim side padding further so nothing overflows the
       viewport (the root cause of the runaway horizontal scroll). */
    @media (max-width:400px) {
      .products-grid, .theme-modern .products-grid, .theme-elegant .products-grid { padding:.75rem; }
      .category-bar, .subcategory-bar, .section-header, .menu-nav, .rec-strip { padding-left:.75rem; padding-right:.75rem; }
      .nav-actions { gap:.4rem; }
      .icon-btn, .cart-btn, .profile-btn { width:38px; height:38px; }
    }

    /* ── RTL mirrors ─────────────────────────────────────────────────────────
       The rules above are authored LTR-first with physical left/right. Under
       <html dir="rtl"> (Persian/Arabic menus) those mirror the wrong way and the
       cards look broken (e.g. tags overlapping the favourite heart). Flip the
       handful that use physical directions so everything lays out correctly. */
    html[dir="rtl"] .nav-actions,
    html[dir="rtl"] .item-count { margin-left:0; margin-right:auto; }
    html[dir="rtl"] .cart-badge { right:auto; left:-4px; }
    html[dir="rtl"] .card-tags { left:auto; right:.75rem; }
    html[dir="rtl"] .theme-elegant .qty-control { margin-left:0; margin-right:.75rem; }
    html[dir="rtl"] .layout-sidebar .category-bar { border-right:none; border-left:1px solid var(--border); }

    /* ── Currency / number-overflow safety ───────────────────────────────────
       Rial & Toman prices run to millions ("۱٬۲۵۰٬۰۰۰"), which used to overflow
       price chips, add buttons and the floating cart bar. Keep prices on tidy
       tabular figures, let them shrink/wrap instead of overflowing, and never
       let the floating cart grow wider than the viewport. */
    .card-price, .rec-card-price, .qty-num, .item-count, .cart-badge, .floating-cart {
      font-variant-numeric: tabular-nums; }
    .card-price { line-height:1.15; }
    .theme-elegant .card-price { white-space:normal; overflow-wrap:anywhere; }
    .card-footer { row-gap:.5rem; }
    .add-btn, .qty-control { flex-shrink:0; }
    .floating-cart {
      max-width:calc(100vw - 2rem); box-sizing:border-box; flex-wrap:wrap;
      justify-content:center; gap:.6rem 1.25rem; text-align:center; padding:.9rem 1.5rem; }
    .floating-cart > * { min-width:0; overflow-wrap:anywhere; }
    @media (max-width:480px) {
      .floating-cart { left:1rem; right:1rem; transform:none; width:auto; }
      @keyframes slideUp { from { transform:translateY(20px); opacity:0; } to { transform:translateY(0); opacity:1; } }
      .floating-cart:hover { transform:translateY(-2px); }
    }
  `;
}
